import { sumCents } from './money';
import type { AllocationDraft, Cents, CollectableInvoice, PaymentDraft } from './types';

/**
 * The collection rules, mirrored from the backend so the agent gets immediate
 * feedback instead of a round trip and a rejection.
 *
 * Pure functions only — no React, no HTTP. The server re-validates all of it;
 * this exists so the "Issue receipt" button is disabled with a reason showing,
 * rather than enabled and then failing.
 */

export interface CollectionBalance {
  collectedCents: Cents;
  cashCents: Cents;
  chequeCents: Cents;
  allocatedCents: Cents;
  /** Positive = money still to assign. Negative = over-assigned. */
  remainingCents: Cents;
  isBalanced: boolean;
}

export function computeBalance(
  payments: readonly PaymentDraft[],
  allocations: readonly AllocationDraft[],
): CollectionBalance {
  const cashCents = sumCents(
    payments.filter((p) => p.method === 'CASH').map((p) => p.amountCents),
  );
  const chequeCents = sumCents(
    payments.filter((p) => p.method === 'CHEQUE').map((p) => p.amountCents),
  );
  const collectedCents = cashCents + chequeCents;
  const allocatedCents = sumCents(allocations.map((a) => a.amountCents));

  return {
    collectedCents,
    cashCents,
    chequeCents,
    allocatedCents,
    remainingCents: collectedCents - allocatedCents,
    // The governing rule: a receipt must balance exactly. No surplus held as
    // credit, no shortfall.
    isBalanced: collectedCents > 0 && collectedCents === allocatedCents,
  };
}

export type CollectionIssueCode =
  | 'NO_PAYMENTS'
  | 'INVALID_PAYMENT_AMOUNT'
  | 'CHEQUE_DETAILS_REQUIRED'
  | 'DUPLICATE_CHEQUE'
  | 'NO_ALLOCATIONS'
  | 'ALLOCATION_EXCEEDS_OUTSTANDING'
  | 'UNDER_ALLOCATED'
  | 'OVER_ALLOCATED';

export interface CollectionIssue {
  code: CollectionIssueCode;
  /** Which payment row, when the problem is a payment. */
  paymentKey?: string;
  /** Which invoice, when the problem is an allocation. */
  invoiceId?: number;
  /** Interpolation values for the translated message. */
  values?: Record<string, string | number>;
}

/**
 * Everything wrong with the draft, as a list rather than a first-failure —
 * the agent should see all of it at once and fix it in one pass.
 */
export function validateDraft(
  payments: readonly PaymentDraft[],
  allocations: readonly AllocationDraft[],
  invoices: readonly CollectableInvoice[],
): CollectionIssue[] {
  const issues: CollectionIssue[] = [];
  const invoiceById = new Map(invoices.map((invoice) => [invoice.id, invoice]));

  if (payments.length === 0) {
    issues.push({ code: 'NO_PAYMENTS' });
  }

  for (const payment of payments) {
    if (payment.amountCents <= 0) {
      issues.push({ code: 'INVALID_PAYMENT_AMOUNT', paymentKey: payment.key });
    }

    if (payment.method === 'CHEQUE') {
      const missing: string[] = [];
      if (!payment.chequeNo.trim()) missing.push('chequeNo');
      if (!payment.chequeDate) missing.push('chequeDate');
      if (!payment.bankId) missing.push('bank');

      if (missing.length > 0) {
        issues.push({
          code: 'CHEQUE_DETAILS_REQUIRED',
          paymentKey: payment.key,
          values: { fields: missing.join(', ') },
        });
      }
    }
  }

  // The same cheque twice on one receipt is nearly always double entry.
  const chequeKeys = payments
    .filter((p) => p.method === 'CHEQUE' && p.bankId && p.chequeNo.trim())
    .map((p) => ({ key: p.key, id: `${p.bankId}:${p.chequeNo.trim().toUpperCase()}` }));

  for (const cheque of chequeKeys) {
    if (chequeKeys.filter((other) => other.id === cheque.id).length > 1) {
      issues.push({ code: 'DUPLICATE_CHEQUE', paymentKey: cheque.key });
    }
  }

  const positiveAllocations = allocations.filter((a) => a.amountCents > 0);
  if (positiveAllocations.length === 0) {
    issues.push({ code: 'NO_ALLOCATIONS' });
  }

  for (const allocation of positiveAllocations) {
    const invoice = invoiceById.get(allocation.invoiceId);
    if (!invoice) continue;

    if (allocation.amountCents > invoice.outstandingCents) {
      issues.push({
        code: 'ALLOCATION_EXCEEDS_OUTSTANDING',
        invoiceId: allocation.invoiceId,
        values: { invoiceNo: invoice.invoiceNo },
      });
    }
  }

  const balance = computeBalance(payments, positiveAllocations);
  if (balance.collectedCents > 0 && balance.remainingCents > 0) {
    issues.push({ code: 'UNDER_ALLOCATED', values: { amount: balance.remainingCents } });
  }
  if (balance.remainingCents < 0) {
    issues.push({ code: 'OVER_ALLOCATED', values: { amount: -balance.remainingCents } });
  }

  return issues;
}

/**
 * Distributes the collected amount across invoices, oldest due date first.
 *
 * This is the button that saves the agent the arithmetic. It settles each
 * invoice in turn until the money runs out, leaving a partial allocation on
 * whichever invoice the money stops in — which is how the paper receipts
 * behave (see the sample sheet's receipt 11291).
 */
export function autoAllocate(
  collectedCents: Cents,
  invoices: readonly CollectableInvoice[],
): AllocationDraft[] {
  let remaining = collectedCents;

  const ordered = [...invoices].sort((a, b) => {
    if (a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
    return a.id - b.id;
  });

  const allocations: AllocationDraft[] = [];

  for (const invoice of ordered) {
    if (remaining <= 0) break;

    const amount = Math.min(remaining, invoice.outstandingCents);
    if (amount > 0) {
      allocations.push({ invoiceId: invoice.id, amountCents: amount });
      remaining -= amount;
    }
  }

  return allocations;
}

/**
 * Splits each allocation into its cash-covered and cheque-covered parts, cash
 * first. Used for the customer slip so it shows the same Cash column the
 * printed report will.
 */
export function cashFirstWaterfall(
  allocations: readonly AllocationDraft[],
  cashCents: Cents,
): Cents[] {
  let remainingCash = cashCents;

  return allocations.map((allocation) => {
    const fromCash = Math.min(remainingCash, allocation.amountCents);
    remainingCash -= fromCash;
    return fromCash;
  });
}
